import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { DisabledFieldsTooltip, INITIAL_TASK_VALUES, TaskTooltip } from "@Components/assessment/assessmentConstants";
import CrossIcon from "@Components/shared/CrossIcon";
import InfoTooltip from "@Components/shared/InfoTooltip";
import TickIcon from "@Components/shared/TickIcon";
import { useActivityCategoriesTree } from "@Components/shared/hooks/useActivityCategoriesTree";
import SessionManager from "@Core/session";
import { IActivityDefinition } from "@Models/IActivityDetails";
import { IAssessmentDetails } from "@Models/IAssessmentDetails";
import { ITaskDetails } from "@Models/ITaskDetails";
import { IUserDetails, IUserMinimal, isIUserDetails } from "@Models/IUserDetails";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import { DateTime, IColumnData, ITreeItem, buildTree, filterTreeNode, getSystemId, useSearchParams } from "@Utils";
import { MinusCircleOutlined } from "@ant-design/icons";
import { FormInstance, Popconfirm } from "antd";
import { DefaultOptionType } from "antd/lib/select";
import { IUseAssessmentData } from "../types";
import { populateInitialValues } from "./../helpers";
import { useCreateAssessment, useUpdateAssessment } from "./../mutations";
import { useAssessmentQuery, useAssessorAndGroupsQuery } from "./../queries";

type IMissingOptions = Partial<Record<keyof IAssessmentDetails, boolean>>;

interface IUseNewAssessmentData extends IUseAssessmentData<ITaskDetails, IAssessmentDetails> {
    initialValues: IAssessmentDetails;
    formInstance: React.MutableRefObject<FormInstance>;
    taskRows: {
        addRow: () => void;
        rows: ITaskDetails[];
    };

    // values type is not IAssessmentDetails because the values captured by the form may not match the model
    onClear: () => void;
    onFinish: (values: Record<keyof IAssessmentDetails, unknown>, resetFields: () => void) => void;
    onValuesChange: (changedValues: unknown, values: IAssessmentDetails) => void;
}

const useNewAssessment = (): IUseNewAssessmentData => {
    // ** State ** //
    const [assessorGroups, setAssessorGroups] = useState<string[]>([]);
    const [taskRows, setTaskRows] = useState<ITaskDetails[]>([INITIAL_TASK_VALUES]);
    const [activitySelectionsSet, setActivitySelectionsSet] = useState<Set<string>>(new Set());
    const [searchParams, setSearchParams] = useSearchParams();
    const assessmentParam = searchParams.get("assessment");

    const formInstance = useRef<FormInstance>();
    const hasRestoredValuesRef = useRef(false);
    const hasPopulatedInitialAssessorGroupsRef = useRef(false);
    const missingOptionsRef = useRef<IMissingOptions>({});

    // ** Hooks ** //
    const { userId } = SessionManager?.getServiceUser();
    const { mutate: create, isLoading: isCreating } = useCreateAssessment(userId);
    const { mutate: update, isLoading: isUpdating } = useUpdateAssessment(assessmentParam);

    const {
        assessors,
        groupIdsByUser,
        groups,
        isFetched: areUserIdsFetched,
        isFetching: areAssessorsFetching,
        isLoading: areAssessorsLoading,
        templates,
        userIdsByGroup,
        usersMap,
    } = useAssessorAndGroupsQuery(userId);

    const { data: assessment, isFetched } = useAssessmentQuery(assessmentParam);

    const hasAssessment = !!assessment;
    const isPastStartDate = hasAssessment && DateTime(assessment.startDate).isPast();
    const isPastEndDate = hasAssessment && DateTime(assessment.endDate).isPast();
    const isPastSubmissionDate =
        hasAssessment &&
        assessment?.endDate &&
        DateTime(DateTime(assessment?.endDate).addDays(assessment?.lateSubmissions)).isPast();

    const disabledActivities = (record: IActivityDefinition) => activitySelectionsSet.has(record.activityId);
    const activityCategoriesTree = useActivityCategoriesTree("", disabledActivities);

    const createAssessment = async (
        newAssessment: Record<keyof IAssessmentDetails, unknown>,
        resetFields: () => void
    ) => {
        // we need to format the users in the same form as the model
        // we also need to make sure that the users are unique (some users could be in multiple groups)
        const uniqueUsers = new Set<string>();
        const users: IUserMinimal[] = [];
        [...(newAssessment.users as string[])].forEach((uid: string | { value: string }) => {
            const userIdValue = typeof uid === "string" ? uid : uid.value;
            const formattedUserId = userIdValue?.includes("¦") ? userIdValue.split("¦")[1] : userIdValue;

            if (uniqueUsers.has(formattedUserId)) return;
            uniqueUsers.add(formattedUserId);

            users.push({
                // we need to remove the prefix
                userId: formattedUserId,
                fullName: "", // fullName isn't being used but the key is required
            });
        });

        newAssessment.users = users;
        // we want to make sure the start date isn't in the past and before the end date
        newAssessment.startDate = moment(newAssessment.startDate).endOf("day").startOf("minute");
        // we want to make sure the end date is at the end of day
        newAssessment.endDate = moment(newAssessment.endDate).endOf("day");

        if (!isTasksDisabled) {
            // cascader creates an array of ids. we only want the last id in the array
            newAssessment.tasks = (newAssessment.tasks as Record<keyof ITaskDetails, unknown>[])?.map((task) => ({
                ...task,
                activityId: task.activityId?.[(task.activityId as string[]).length - 1],
            }));
        }

        const model = { model: { ...newAssessment, assessmentId: assessmentParam } as IAssessmentDetails };

        !!assessment
            ? update(model)
            : create(model, {
                  onSuccess: () => {
                      setSearchParams({ tab: "current" });
                      setAssessorGroups([]);
                      resetFields();
                  },
              });
    };

    const populateAssessorGroups = useCallback(
        (key: IAssessmentDetails["owner"]) => {
            const activeGroupIds = [];
            userIdsByGroup.forEach((group, groupId) => {
                if (group.has(key)) activeGroupIds.push(groupId);
            });
            setAssessorGroups(activeGroupIds);
            if (!hasPopulatedInitialAssessorGroupsRef.current) hasPopulatedInitialAssessorGroupsRef.current = true;
        },
        [userIdsByGroup]
    );

    const handleAssessorChange = (_: string, options: DefaultOptionType) => {
        if (!options) return setAssessorGroups([]);

        populateAssessorGroups(options.key);
    };

    const handleFinish = (values: Record<keyof IAssessmentDetails, unknown>, resetFields: () => void) =>
        createAssessment(values, resetFields);

    const handleAddRow = () => {
        setTaskRows([...taskRows, INITIAL_TASK_VALUES]);
    };

    const handleDeleteRow = (_: ITaskDetails, index: number) => {
        const newTaskRows = [...taskRows];
        newTaskRows.splice(index, 1);
        setTaskRows(newTaskRows);
    };

    const handleClear = () => {
        setAssessorGroups([]);
    };

    const handleValuesChange = (
        changedValues: Record<keyof IAssessmentDetails, unknown>,
        values: IAssessmentDetails
    ) => {
        if ("tasks" in changedValues) {
            const ids = new Set(
                values.tasks.map((task) => task?.activityId?.[task.activityId?.length - 1]).filter((t) => t)
            );
            setActivitySelectionsSet(ids);
        }
    };

    const filteredGroups = groups.filter((group) => assessorGroups.includes(group.groupId));
    const nestedGroupIds = [];

    const buildBranch = (group: IUserGroupDetails, parentGroupId: IUserGroupDetails["parentGroupId"], level = 0) => {
        //if (group.parentGroupId !== parentGroupId) return [];

        const userIds = userIdsByGroup.get(group.groupId) || [];
        const userData: IUserDetails[] = Array.from(userIds).flatMap((id) => usersMap?.get(id));

        const subGroupIds = groups.filter((g) => g.parentGroupId === group.groupId).map((g) => g.groupId);
        nestedGroupIds.push(subGroupIds);

        if (!userData) return [];

        return buildTree({
            data: [groups.filter((g) => g.parentGroupId === group.groupId), userData].flat(),
            fields: {
                children: (datum) => (isIUserDetails(datum) ? null : buildBranch(datum, group.groupId, level + 1)),
                prefix: `${group.groupId}¦`,
                title: (datum) => (isIUserDetails(datum) ? datum.fullName : datum.name),
                value: (datum) => (isIUserDetails(datum) ? datum.userId : datum.groupId),
            },
        });
    };

    const groupTreeDataWithDuplicates = buildTree({
        data: filteredGroups,
        fields: {
            children: (groupDetails) => buildBranch(groupDetails, getSystemId()),
            title: "name",
            value: "groupId",
        },
    });

    //filter out all the groups that are nested within other groups in the tree data
    const groupTreeData = groupTreeDataWithDuplicates.filter((group) => !nestedGroupIds.flat().includes(group.key));

    const disabledFields = {
        description: { isDisabled: false },
        endDate: { isDisabled: isPastEndDate, message: isPastEndDate ? DisabledFieldsTooltip.PastEndDate : null },
        feedbackTemplateId: {
            isDisabled: isPastStartDate || groupTreeData?.length === 0,
            message: isPastStartDate ? DisabledFieldsTooltip.PastStartDate : null,
        },
        lateSubmissions: { isDisabled: isPastSubmissionDate, message: DisabledFieldsTooltip.PastSubmissionDate },
        name: { isDisabled: false },
        owner: { isDisabled: isPastStartDate, message: isPastStartDate ? DisabledFieldsTooltip.PastStartDate : null },
        startDate: {
            isDisabled: isPastStartDate,
            message: isPastStartDate ? DisabledFieldsTooltip.PastStartDate : null,
        },
        tasks: { isDisabled: isPastStartDate, message: isPastStartDate ? DisabledFieldsTooltip.PastStartDate : null },
        users: {
            isDisabled: isPastStartDate || groupTreeData?.length === 0,
            message: isPastStartDate ? DisabledFieldsTooltip.PastStartDate : null,
        },
    };

    const isTasksDisabled = disabledFields.tasks.isDisabled;
    const columnData: IColumnData<ITaskDetails>[] = [
        {
            key: "activityId",
            title: "Activity",
            inputType: "cascader",
            props: {
                options: activityCategoriesTree,
                showSearch: { filterTreeNode, matchInputWidth: true },
            },
            rules: [{ required: true, message: "Please select an activity" }],
        },
        {
            key: "quantity",
            inputType: "number",
            rules: [{ required: true }],
            messageVariables: { name: "Quantity" },
        },
        {
            key: "recorded",
            inputType: "switch",
            render: isTasksDisabled ? (_, task) => (task.recorded ? <TickIcon /> : <CrossIcon />) : undefined,
            title: <InfoTooltip text="Recorded" title={TaskTooltip.Recorded} />,
        },
        {
            key: "institutional",
            inputType: "switch",
            render: isTasksDisabled ? (_, task) => (task.recorded ? <TickIcon /> : <CrossIcon />) : undefined,
            title: <InfoTooltip text="Institutional" title={TaskTooltip.Institutional} />,
        },
        {
            key: "automated",
            inputType: "switch",
            render: isTasksDisabled ? (_, task) => (task.recorded ? <TickIcon /> : <CrossIcon />) : undefined,
            title: <InfoTooltip text="Automated" title={TaskTooltip.Automated} />,
        },
    ];

    if (!isTasksDisabled) {
        columnData.push({
            key: "delete",
            title: "",
            render: (_, record, index) =>
                taskRows.length > 1 ? (
                    <Popconfirm
                        cancelButtonProps={{ className: "secondary" }}
                        title="Remove this task?"
                        onConfirm={() => handleDeleteRow(record, index)}
                    >
                        <MinusCircleOutlined />
                    </Popconfirm>
                ) : null,
        });
    }

    const populateMissingOptions = () => {
        const missingOptions: IMissingOptions = {};
        missingOptions.feedbackTemplateId = !assessment.feedbackTemplateId
            ? false
            : !templates.map((t) => t.templateId).includes(assessment.feedbackTemplateId);
        missingOptions.owner = !assessment.owner ? false : !assessors.map((a) => a.userId).includes(assessment.owner);
        missingOptions.users = !assessment.users ? false : assessment.users.some((u) => !usersMap.get(u.userId));
        missingOptionsRef.current = missingOptions;
        return missingOptions;
    };

    const initialValues = populateInitialValues({
        assessment,
        assessorGroups,
        groupIdsByUser,
        isReady: !hasAssessment || !areUserIdsFetched || assessorGroups.length === 0,
        populateMissingOptions,
    });

    useEffect(() => {
        // set the values  when the assessment is initially populated
        if (isFetched && !!assessment && !!formInstance.current && !hasRestoredValuesRef.current) {
            // sets the task rows only once
            setActivitySelectionsSet(new Set(assessment.tasks.map((a) => a.activityId)));
            const tasksIds = assessment.tasks.map((t) => t.activityId);

            // this map is needed to correctly populate the cascader
            const activeActivityMap = new Map();
            activityCategoriesTree.forEach((branch) => {
                branch?.children?.forEach((childBranch) =>
                    childBranch?.children?.forEach((child) => {
                        if (tasksIds.includes(child.key)) {
                            const key: keyof ITreeItem = isTasksDisabled ? "title" : "key";
                            const value = [branch[key], childBranch[key], child[key]];
                            activeActivityMap.set(child.key, isTasksDisabled ? value.join(" / ") : value);
                        }
                    })
                );
            });

            const formattedTasks = assessment.tasks.map((task) => ({
                ...task,
                activityId: activeActivityMap.get(task.activityId),
            }));
            setTaskRows(formattedTasks);
            formInstance.current.setFieldValue("tasks", formattedTasks);
            hasRestoredValuesRef.current = true;
        }
    }, [activityCategoriesTree, assessment, isFetched, isTasksDisabled]);

    useEffect(() => {
        // sets the populateAssessorGroups when the usersmap is initially populated
        const isExistingAssessmentReady = !!assessment && areUserIdsFetched;
        const isNewAssessmentReady = !assessmentParam && areUserIdsFetched;
        if ((!isExistingAssessmentReady && !isNewAssessmentReady) || !initialValues.owner) return;
        populateAssessorGroups(initialValues.owner);
    }, [areUserIdsFetched, assessment, assessmentParam, initialValues.owner, populateAssessorGroups]);

    const isDataLoading = !areUserIdsFetched || areAssessorsFetching || areAssessorsLoading;

    const { current: missingOptions } = missingOptionsRef;

    const isLoading = !!assessmentParam
        ? !isFetched || !areUserIdsFetched || !hasPopulatedInitialAssessorGroupsRef.current
        : !areUserIdsFetched;

    return {
        columnData,
        data: {
            feedbackTemplateId: {
                list: templates,
                isLoading: isDataLoading,
                messages: missingOptions?.feedbackTemplateId
                    ? ["A previously selected template is no longer available"]
                    : [],
            },
            // assessor
            owner: {
                list: assessors,
                onChange: handleAssessorChange,
                isLoading: isDataLoading,
                messages: missingOptions?.owner ? ["A previously selected assessor is no longer available"] : [],
            },
            users: {
                list: groupTreeData ?? [],
                isLoading: isDataLoading,
                messages: missingOptions?.users ? ["A previously selected user is no longer available"] : [],
            },
        },
        disabledFields,
        initialValues,
        isLoading,
        onClear: handleClear,
        onFinish: handleFinish,
        isSubmitting: isUpdating || isCreating,
        onValuesChange: handleValuesChange,
        formInstance,
        taskRows: { addRow: handleAddRow, rows: taskRows },
    };
};

export default useNewAssessment;
